import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Bot, Play, Square, Settings, Shield,
  Zap, AlertTriangle, Activity, TrendingUp,
  RefreshCw, CheckCircle, Target,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Layout from '../components/Layout';

const P  = '#C29500';
const P2 = '#E8C040';
const G  = '#E8C040';
const R  = '#EF4444';
const C  = '#06B6D4';

const CARD = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 12,
};

const INNER = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 10,
};

const SYMBOLS = [
  'EURUSD','GBPUSD','AUDUSD','NZDUSD','USDCAD','USDCHF','USDJPY',
  'GBPJPY','EURJPY','AUDJPY','CADJPY','NZDJPY','CHFJPY',
  'GBPAUD','GBPCAD','GBPCHF','GBPNZD',
  'EURCAD','EURGBP','EURAUD','EURCHF','EURNZD',
  'AUDCAD','AUDNZD','AUDCHF',
  'NZDCAD','NZDCHF',
  'XAUUSD',
];

const DEFAULT_SETTINGS = {
  risk_percent       : 1,
  max_open_trades    : 3,
  loss_limit         : 0,
  profit_target      : 0,
  smart_tp_percent   : 1,
  smart_loss_percent : 0.5,
  stop_loss_pips     : 20,
  take_profit_pips   : 60,
  news_guard         : true,
  tg_notifications   : true,
  gold_scalp_mode    : false,
  symbols            : 'EURUSD,GBPUSD,AUDUSD',
};

function Toggle({ checked, onChange }) {
  return (
    <motion.button
      whileTap={{ scale: 0.9 }}
      onClick={onChange}
      style={{
        width: 44, height: 24, borderRadius: 12, cursor: 'pointer',
        background: checked ? `linear-gradient(135deg,${P},${P2})` : 'rgba(255,255,255,0.1)',
        border: 'none', position: 'relative', flexShrink: 0,
        transition: 'background .25s',
      }}>
      <motion.div
        animate={{ x: checked ? 22 : 2 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        style={{ width: 20, height: 20, background: '#fff', borderRadius: '50%', position: 'absolute', top: 2 }}/>
    </motion.button>
  );
}

export default function BotControl() {
  const [botData,         setBotData]         = useState(null);
  const [settings,        setSettings]        = useState(DEFAULT_SETTINGS);
  const [loading,         setLoading]         = useState(true);
  const [saving,          setSaving]          = useState(false);
  const [toggling,        setToggling]        = useState(false);
  const [selectedSymbols, setSelectedSymbols] = useState(['EURUSD','GBPUSD','AUDUSD']);
  const [saved,           setSaved]           = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/bot/settings');
      const d   = res.data;
      setBotData(d);
      setSettings({
        risk_percent       : d.risk_percent        ?? 1,
        max_open_trades    : d.max_open_trades     ?? 3,
        loss_limit         : Math.abs(d.loss_limit ?? 0),
        profit_target      : d.profit_target       ?? 0,
        smart_tp_percent   : d.smart_tp_percent    ?? 1,
        smart_loss_percent : d.smart_loss_percent  ?? 0.5,
        stop_loss_pips     : d.stop_loss_pips      ?? 20,
        take_profit_pips   : d.take_profit_pips    ?? 60,
        news_guard         : d.news_guard          ?? true,
        tg_notifications   : d.tg_notifications   ?? true,
        gold_scalp_mode    : d.gold_scalp_mode     ?? false,
        symbols            : d.symbols             ?? 'EURUSD,GBPUSD,AUDUSD',
      });
      if (d.symbols) setSelectedSymbols(d.symbols.split(',').filter(Boolean));
    } catch {
      toast.error('Failed to load bot settings');
    } finally {
      setLoading(false);
    }
  };

  const num = (val, fb = 0) => { const n = parseFloat(val); return isNaN(n) ? fb : n; };
  const int = (val, fb = 0) => { const n = parseInt(val);   return isNaN(n) ? fb : n; };

  const toggleSymbol = (sym) =>
    setSelectedSymbols(prev => prev.includes(sym) ? prev.filter(s => s !== sym) : [...prev, sym]);

  const saveSettings = async () => {
    if (selectedSymbols.length === 0) { toast.error('Select at least one trading pair'); return; }
    setSaving(true);
    try {
      await api.put('/bot/settings', {
        ...settings,
        loss_limit : settings.loss_limit > 0 ? -settings.loss_limit : 0,
        symbols    : selectedSymbols.join(','),
      });
      setSaved(true);
      toast.success('Settings saved!');
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleBot = async () => {
    setToggling(true);
    try {
      if (botData?.is_running) {
        await api.post('/bot/stop');
        setBotData({ ...botData, is_running: false });
        toast.success('Bot stopped');
      } else {
        await api.post('/bot/start');
        setBotData({ ...botData, is_running: true });
        toast.success('Bot started! 🚀');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setToggling(false);
    }
  };

  if (loading) return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 36, height: 36, border: `3px solid ${P}`,
            borderTopColor: 'transparent', borderRadius: '50%',
            animation: 'spin .7s linear infinite', margin: '0 auto 12px',
          }}/>
          <p style={{ color: '#475569', fontSize: 14 }}>Loading bot settings…</p>
        </div>
      </div>
    </Layout>
  );

  const isRunning = botData?.is_running;

  return (
    <Layout>
      <div style={{ padding: 28, maxWidth: 1280, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: -0.5 }}>Bot Control</h1>
            <p style={{ fontSize: 13, color: '#64748B', margin: '6px 0 0' }}>Manage your AI trading bot</p>
          </div>
          <motion.button whileHover={{ rotate: 180 }} transition={{ duration: 0.3 }}
            onClick={load}
            style={{
              padding: 10, borderRadius: 10, cursor: 'pointer',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
              color: '#64748B', display: 'flex', alignItems: 'center',
            }}>
            <RefreshCw size={16}/>
          </motion.button>
        </motion.div>

        <div className="bc-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20 }}>

          {/* Bot Status Card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            style={{
              ...CARD, padding: 28,
              position: 'relative', overflow: 'hidden',
            }}>
            <div style={{
              position: 'absolute', inset: 0, opacity: 0.04,
              background: isRunning ? G : R, transition: 'background 1s',
            }}/>

            <div style={{ position: 'relative', textAlign: 'center' }}>
              <div style={{ position: 'relative', display: 'inline-block', marginBottom: 20 }}>
                <motion.div
                  animate={isRunning ? { boxShadow: [`0 0 20px ${G}40`, `0 0 40px ${G}70`, `0 0 20px ${G}40`] } : {}}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{
                    width: 100, height: 100, borderRadius: '50%', margin: '0 auto',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isRunning ? `${G}12` : `${R}12`,
                    border: `2px solid ${isRunning ? G : R}30`,
                  }}>
                  <Bot size={44} style={{ color: isRunning ? G : R }}/>
                </motion.div>
                <div style={{
                  position: 'absolute', top: 4, right: 4,
                  width: 14, height: 14, borderRadius: '50%',
                  background: isRunning ? G : R,
                }}>
                  {isRunning && (
                    <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: G, animation: 'waping 1.5s infinite' }}/>
                  )}
                </div>
              </div>

              <h2 style={{ fontSize: 26, fontWeight: 900, color: isRunning ? G : R, margin: '0 0 6px' }}>
                {isRunning ? 'RUNNING' : 'STOPPED'}
              </h2>
              <p style={{ fontSize: 13, color: '#475569', marginBottom: 20 }}>
                {isRunning ? 'Bot is actively trading' : 'Bot is not trading'}
              </p>

              {isRunning && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                  <div style={{ ...INNER, padding: '10px 14px' }}>
                    <p style={{ fontSize: 10, color: '#475569', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>P&L</p>
                    <p style={{ fontSize: 16, fontWeight: 800, color: (botData.pnl || 0) >= 0 ? G : R, margin: 0 }}>
                      ${botData.pnl ?? 0}
                    </p>
                  </div>
                  <div style={{ ...INNER, padding: '10px 14px' }}>
                    <p style={{ fontSize: 10, color: '#475569', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Runtime</p>
                    <p style={{ fontSize: 16, fontWeight: 800, color: '#fff', margin: 0 }}>{botData.runtime ?? '0:00:00'}</p>
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
                {[
                  { label: 'Risk',       value: `${settings.risk_percent}%`, color: C },
                  { label: 'Max Trades', value: settings.max_open_trades,    color: P2 },
                ].map((s, i) => (
                  <div key={i} style={{ ...INNER, padding: '12px 14px' }}>
                    <p style={{ fontSize: 10, color: '#475569', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</p>
                    <p style={{ fontSize: 18, fontWeight: 900, color: s.color, margin: 0 }}>{s.value}</p>
                  </div>
                ))}
              </div>

              <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={toggleBot} disabled={toggling}
                style={{
                  width: '100%', padding: '14px 0', borderRadius: 12, border: 'none',
                  cursor: toggling ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  fontSize: 14, fontWeight: 700,
                  background: isRunning ? `${R}20` : `linear-gradient(135deg,${P},${P2})`,
                  border: isRunning ? `1px solid ${R}30` : 'none',
                  color: isRunning ? R : '#000',
                }}>
                {toggling ? (
                  <div style={{ width: 18, height: 18, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite' }}/>
                ) : isRunning ? (
                  <><Square size={16}/> Stop Bot</>
                ) : (
                  <><Play size={16}/> Start Bot</>
                )}
              </motion.button>

              {!isRunning && (
                <p style={{ fontSize: 11, color: '#F59E0B', marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                  <AlertTriangle size={11}/> Requires active subscription
                </p>
              )}
              {isRunning && (
                <p style={{ fontSize: 11, color: G, marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                  <CheckCircle size={11}/> Settings apply instantly
                </p>
              )}
            </div>
          </motion.div>

          {/* Settings Panel */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ ...CARD, padding: 28 }}>

            <p style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: '0 0 24px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Settings size={16} style={{ color: P2 }}/> Bot Settings
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Risk Slider */}
              <div style={{ ...INNER, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Shield size={14} style={{ color: C }}/>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#CBD5E1' }}>Risk Per Trade</span>
                  </div>
                  <span style={{ fontSize: 18, fontWeight: 900, color: P2 }}>{settings.risk_percent}%</span>
                </div>
                <input type="range" min="0.5" max="5" step="0.5"
                  value={settings.risk_percent}
                  onChange={e => setSettings({ ...settings, risk_percent: parseFloat(e.target.value) })}
                  style={{ width: '100%', accentColor: P, height: 6 }}/>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                  <span style={{ fontSize: 10, color: '#475569' }}>0.5% Safe</span>
                  <span style={{ fontSize: 10, color: '#475569' }}>2.5% Moderate</span>
                  <span style={{ fontSize: 10, color: '#475569' }}>5% Aggressive</span>
                </div>
              </div>

              {/* Max Trades Slider */}
              <div style={{ ...INNER, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Zap size={14} style={{ color: P2 }}/>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#CBD5E1' }}>Max Open Trades</span>
                  </div>
                  <span style={{ fontSize: 18, fontWeight: 900, color: P2 }}>{settings.max_open_trades}</span>
                </div>
                <input type="range" min="1" max="10" step="1"
                  value={settings.max_open_trades}
                  onChange={e => setSettings({ ...settings, max_open_trades: parseInt(e.target.value) })}
                  style={{ width: '100%', accentColor: P, height: 6 }}/>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                  <span style={{ fontSize: 10, color: '#475569' }}>1 Conservative</span>
                  <span style={{ fontSize: 10, color: '#475569' }}>10 Maximum</span>
                </div>
              </div>

              {/* Loss Limit + Profit Target */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ ...INNER, padding: 14 }}>
                  <label style={{ fontSize: 11, color: '#64748B', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
                    <TrendingUp size={11} style={{ color: R }}/> Daily Loss Limit
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ color: R, fontWeight: 700, fontSize: 13 }}>-$</span>
                    <input type="number" min="0"
                      value={settings.loss_limit || ''}
                      onChange={e => setSettings({ ...settings, loss_limit: num(e.target.value) })}
                      placeholder="0 = auto 3%"
                      style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: 14, fontWeight: 700 }}/>
                  </div>
                </div>
                <div style={{ ...INNER, padding: 14 }}>
                  <label style={{ fontSize: 11, color: '#64748B', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
                    <TrendingUp size={11} style={{ color: G }}/> Daily Profit Target
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ color: G, fontWeight: 700, fontSize: 13 }}>+$</span>
                    <input type="number" min="0"
                      value={settings.profit_target || ''}
                      onChange={e => setSettings({ ...settings, profit_target: num(e.target.value) })}
                      placeholder="0 = auto 5%"
                      style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: 14, fontWeight: 700 }}/>
                  </div>
                </div>
              </div>

              {/* SL/TP/Smart */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
                {[
                  { label: 'SL Pips',     key: 'stop_loss_pips',     placeholder: '20',  color: R,  min: 5,   max: 100, step: 1,   parser: int, fallback: 20  },
                  { label: 'TP Pips',     key: 'take_profit_pips',   placeholder: '60',  color: G,  min: 5,   max: 300, step: 1,   parser: int, fallback: 60  },
                  { label: 'Smart TP %',  key: 'smart_tp_percent',   placeholder: '1.0', color: P2, min: 0.1, max: 10,  step: 0.1, parser: num, fallback: 1   },
                  { label: 'Smart Loss %',key: 'smart_loss_percent', placeholder: '1.0', color: R,  min: 0.1, max: 10,  step: 0.1, parser: num, fallback: 0.5 },
                ].map(f => (
                  <div key={f.key} style={{ ...INNER, padding: 14 }}>
                    <label style={{ fontSize: 11, color: '#64748B', display: 'block', marginBottom: 8 }}>{f.label}</label>
                    <input type="number" min={f.min} max={f.max} step={f.step}
                      value={settings[f.key] || ''}
                      onChange={e => setSettings({ ...settings, [f.key]: f.parser(e.target.value, f.fallback) })}
                      placeholder={f.placeholder}
                      style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: f.color, fontSize: 16, fontWeight: 800 }}/>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: 11, color: '#334155', marginTop: -6 }}>
                SL/TP in pips per trade · Smart TP/Loss closes when profit/loss hits % of balance
              </p>

              {/* Trading Pairs */}
              <div style={{ ...INNER, padding: 16 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#CBD5E1', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Activity size={14} style={{ color: P2 }}/>
                  Trading Pairs
                  <span style={{ fontSize: 11, color: '#475569', fontWeight: 400 }}>({selectedSymbols.length} selected)</span>
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {SYMBOLS.map(sym => (
                    <motion.button key={sym}
                      whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                      onClick={() => toggleSymbol(sym)}
                      style={{
                        padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                        background: selectedSymbols.includes(sym) ? `linear-gradient(135deg,${P},${P2})` : 'rgba(255,255,255,0.04)',
                        border: selectedSymbols.includes(sym) ? 'none' : '1px solid rgba(255,255,255,0.08)',
                        color: selectedSymbols.includes(sym) ? '#000' : '#64748B',
                      }}>
                      {sym}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Toggles */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { key: 'news_guard',       label: 'News Guard',      desc: 'Pause trading during news events', icon: <Shield size={14} style={{ color: '#F59E0B' }}/> },
                  { key: 'tg_notifications', label: 'Telegram Alerts', desc: 'Get instant trade notifications',  icon: <Activity size={14} style={{ color: C }}/> },
                  { key: 'gold_scalp_mode',  label: 'Gold Scalp Mode', desc: 'XAUUSD tight 15/30-pt quick scalps', icon: <Zap size={14} style={{ color: P2 }}/> },
                ].map(t => (
                  <div key={t.key} style={{ ...INNER, padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      {t.icon}
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 600, color: '#CBD5E1', margin: '0 0 2px' }}>{t.label}</p>
                        <p style={{ fontSize: 11, color: '#475569', margin: 0 }}>{t.desc}</p>
                      </div>
                    </div>
                    <Toggle checked={settings[t.key]} onChange={() => setSettings({ ...settings, [t.key]: !settings[t.key] })}/>
                  </div>
                ))}
              </div>

              {/* Save */}
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={saveSettings} disabled={saving}
                style={{
                  width: '100%', padding: '14px 0', borderRadius: 12, border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  fontSize: 14, fontWeight: 700,
                  background: saved ? `${G}20` : `linear-gradient(135deg,${P},${P2})`,
                  border: saved ? `1px solid ${G}40` : 'none',
                  color: saved ? G : '#000',
                }}>
                {saving ? (
                  <div style={{ width: 18, height: 18, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite' }}/>
                ) : saved ? (
                  <><CheckCircle size={16}/> Settings Saved!</>
                ) : 'Save Settings'}
              </motion.button>

            </div>
          </motion.div>
        </div>

        {/* Info Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginTop: 20 }} className="bc-info">
          {[
            { icon: <Shield size={18}/>,    label: 'Stop Loss',   value: `${settings.stop_loss_pips}p`,     color: C,  desc: 'Per trade SL'   },
            { icon: <TrendingUp size={18}/>, label: 'Take Profit', value: `${settings.take_profit_pips}p`,  color: G,  desc: 'Per trade TP'   },
            { icon: <Zap size={18}/>,        label: 'Smart TP',   value: `${settings.smart_tp_percent}%`,   color: P2, desc: 'Per-trade exit' },
            { icon: <Shield size={18}/>,     label: 'Smart Loss', value: `${settings.smart_loss_percent}%`, color: R,  desc: 'Per-trade guard'},
          ].map((c, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.07 }}
              style={{ ...CARD, padding: 16, textAlign: 'center' }}>
              <div style={{ color: c.color, display: 'flex', justifyContent: 'center', marginBottom: 8 }}>{c.icon}</div>
              <p style={{ fontSize: 20, fontWeight: 900, color: c.color, margin: '0 0 2px' }}>{c.value}</p>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#CBD5E1', margin: '0 0 2px' }}>{c.label}</p>
              <p style={{ fontSize: 11, color: '#475569', margin: 0 }}>{c.desc}</p>
            </motion.div>
          ))}
        </div>

      </div>

      <style>{`
        @media (max-width: 1023px) {
          .bc-grid { grid-template-columns: 1fr !important; }
          .bc-info  { grid-template-columns: repeat(2,1fr) !important; }
        }
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes waping { 0%,100%{transform:scale(1);opacity:.7} 50%{transform:scale(1.4);opacity:0} }
        input[type=range] { cursor: pointer; }
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }
        input::placeholder { color: #334155; }
      `}</style>
    </Layout>
  );
}
